import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import Tooltip from '@adsk/alloy-react-tooltip';
import { useLocale } from '@adsk/alloy-react-locale';
import { useCurrentAccountId } from '@adsk/alloy-provider';
import { useReducerStore } from '@adsk/acs-admin-ui-components-shared-components';
import {
  getTimeZones,
  getCurrencies,
  mapProjectTypesForSelectBox,
} from '@adsk/acs-admin-ui-components-helpers';
import { useGetProjectTypes } from '@adsk/acs-admin-ui-components-hooks';
import CategoryWrapper from '../CategoryWrapper';
import TextField from '../Fields/TextField';
import ProjectTemplateField from '../Fields/ProjectTemplateField';
import DropdownField from '../Fields/DropdownField';
import MonetaryValueField from '../Fields/MonetaryValueField';
import {
  CategorizedContentStoreContext,
  TEMPLATE_PROJECT_TYPE,
} from '../../constants';
import {
  fixProjectType,
  setCurrency,
  setJobNumber,
  setProjectValue,
  setProjectType,
  setTimezone,
  setShowProjectValueError,
} from '../store/actions';
import { getProjectTemplateUrl, validProjectValue } from '../utils';

const AdvancedDetails = ({ isEmsAccount }) => {
  const { dispatch, store } = useReducerStore(CategorizedContentStoreContext);
  const { data, ui } = store;

  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`../../../i18n/${locale}.json`)
  );
  const accountId = useCurrentAccountId();
  const { projectTypes } = useGetProjectTypes(accountId);
  const formattedProjectTypes = useMemo(
    () => mapProjectTypesForSelectBox(projectTypes, localeName),
    [projectTypes, localeName]
  );
  const timeZones = useMemo(() => getTimeZones(localeName), [localeName]);
  const currencies = getCurrencies();

  const timezoneTooltip = (
    <Tooltip
      placement={Tooltip.PLACEMENTS.RIGHT}
      content={T.translate('PROJECT_TIMEZONE_TOOLTIP_CONTENT')}
    >
      <AlertInformationIcon />
    </Tooltip>
  );

  const handleCurrencyChange = (option) => {
    dispatch(setCurrency(option?.value ?? null));
  };

  const handleProjectValueChange = (event) => {
    dispatch(setShowProjectValueError(!validProjectValue(event.target.value)));
    dispatch(setProjectValue(event.target.value || null));
  };

  const handleJobNumberChange = (event) => {
    dispatch(setJobNumber(event.target.value));
  };

  const handleProjectTypeChange = (option) => {
    dispatch(setProjectType(option?.value ?? null));
  };

  const handleTimezoneChange = (option) => {
    dispatch(setTimezone(option?.value ?? null));
  };

  // NOTE: proyectType Fix (must be fixed on the backend)
  useEffect(() => {
    if (ui.localizedType == null || projectTypes == null) return;

    const currentProjectType = projectTypes.find(
      (type) => type.displayName === ui.localizedType
    );

    if (currentProjectType && ui.localizedType !== currentProjectType.value) {
      dispatch(fixProjectType(currentProjectType.value));
    }
  }, [projectTypes, dispatch, ui.localizedType]);

  return (
    <CategoryWrapper
      description={
        isEmsAccount
          ? T.translate('PROJECT_CATEGORY_ADVANCED_DESCRIPTION_EMS')
          : T.translate('PROJECT_CATEGORY_ADVANCED_DESCRIPTION')
      }
      title={T.translate('PROJECT_CATEGORY_ADVANCED')}
    >
      <TextField
        isEditing={ui.isEditing}
        onChange={handleJobNumberChange}
        placeHolder={T.translate('JOB_NUMBER_PLACEHOLDER')}
        title={T.translate('PROJECT_JOB_NUMBER')}
        value={data.jobNumber}
      />
      {!isEmsAccount && (
        <>
          <DropdownField
            isEditing={data.type !== TEMPLATE_PROJECT_TYPE && ui.isEditing}
            onChange={handleProjectTypeChange}
            options={formattedProjectTypes}
            required
            title={T.translate('PROJECT_TYPE')}
            // NOTE: proyectType Fix (must be fixed on the backend)
            value={ui.isEditing ? data.type : ui.localizedType}
          />
          <DropdownField
            clearable
            isEditing={ui.isEditing}
            onChange={handleTimezoneChange}
            options={timeZones}
            searchable
            showSearchIcon
            title={T.translate('PROJECT_TIMEZONE')}
            tooltip={timezoneTooltip}
            value={data.timezone}
            valueFormatter={(value) =>
              timeZones?.find((e) => e.value === value)?.label
            }
          />
          <MonetaryValueField
            currencies={currencies}
            currencyValue={data.projectValue?.currency ?? null}
            errorMessage={
              ui.showProjectValueError ? T.translate('PROJECT_VALUE_ERROR') : ''
            }
            isEditing={ui.isEditing}
            onCurrencyChange={handleCurrencyChange}
            onValueChange={handleProjectValueChange}
            title={T.translate('PROJECT_VALUE')}
            value={data.projectValue?.value ?? ''}
          />
          <ProjectTemplateField
            isArchived={data.sourceTemplate?.isArchived}
            isEditing={ui.isEditing}
            isTemplateMember={data.sourceTemplate?.isTemplateMember}
            placeHolder={T.translate('PROJECT_TEMPLATE_PLACEHOLDER')}
            title={T.translate('PROJECT_TEMPLATE_LABEL')}
            url={getProjectTemplateUrl(data.sourceTemplate?.templateId)}
            value={data.sourceTemplate?.name}
          />
        </>
      )}
    </CategoryWrapper>
  );
};

export default AdvancedDetails;

AdvancedDetails.propTypes = {
  isEmsAccount: PropTypes.bool,
};

AdvancedDetails.defaultProps = {
  isEmsAccount: false,
};
